import { create } from 'zustand';
import { devtools, subscribeWithSelector } from 'zustand/middleware';
import type { TreeSpecies, CommunityTree } from '../types';

// UI state types
interface UIState {
  // Panel states
  sidebarOpen: boolean;
  layerPanelOpen: boolean;
  speciesPanelOpen: boolean;
  treePanelOpen: boolean;
  
  // Active panel
  activePanel: 'species' | 'assessment' | 'community' | 'layers' | null;
  
  // Modal states
  speciesModalOpen: boolean;
  treeDetailsModalOpen: boolean;
  suggestionModalOpen: boolean;
  settingsModalOpen: boolean;
  helpModalOpen: boolean;
  
  // Modal data
  modalData: {
    selectedSpecies?: TreeSpecies;
    selectedTree?: CommunityTree;
    suggestionLocation?: { lng: number; lat: number };
  };
  
  // Loading states
  globalLoading: boolean;
  loadingMessage: string | null;
  
  // Notification system
  notifications: Notification[];
  maxNotifications: number;
  
  // Toast messages
  toasts: Toast[];
  maxToasts: number;
  
  // Form states
  formStates: {
    [formId: string]: {
      isDirty: boolean;
      isSubmitting: boolean;
      errors: Record<string, string>;
      values: Record<string, any>;
    };
  };
  
  // View preferences
  viewPreferences: {
    theme: 'light' | 'dark' | 'auto';
    mapStyle: string;
    defaultZoom: number;
    compactMode: boolean;
    showTooltips: boolean;
    language: string;
  };
  
  // Mobile/responsive states
  isMobile: boolean;
  screenSize: 'xs' | 'sm' | 'md' | 'lg' | 'xl';
  
  // Feature flags
  featureFlags: {
    enableExperimentalFeatures: boolean;
    enableAnalytics: boolean;
    enableOfflineMode: boolean;
    [key: string]: boolean;
  };
}

// Notification types
interface Notification {
  id: string;
  type: 'info' | 'success' | 'warning' | 'error';
  title: string;
  message: string;
  autoClose?: boolean;
  duration?: number;
  timestamp: number;
  actions?: Array<{
    label: string;
    action: () => void;
    style?: 'primary' | 'secondary';
  }>;
}

// Toast types
interface Toast {
  id: string;
  type: 'info' | 'success' | 'warning' | 'error';
  message: string;
  duration?: number;
  timestamp: number;
  position?: 'top-left' | 'top-right' | 'bottom-left' | 'bottom-right';
}

// UI actions
interface UIActions {
  // Panel management
  toggleSidebar: () => void;
  setSidebarOpen: (open: boolean) => void;
  setActivePanel: (panel: UIState['activePanel']) => void;
  togglePanel: (panel: keyof Pick<UIState, 'layerPanelOpen' | 'speciesPanelOpen' | 'treePanelOpen'>) => void;
  closeAllPanels: () => void;
  
  // Modal management
  openModal: (modal: keyof Pick<UIState, 'speciesModalOpen' | 'treeDetailsModalOpen' | 'suggestionModalOpen' | 'settingsModalOpen' | 'helpModalOpen'>, data?: Partial<UIState['modalData']>) => void;
  closeModal: (modal: keyof Pick<UIState, 'speciesModalOpen' | 'treeDetailsModalOpen' | 'suggestionModalOpen' | 'settingsModalOpen' | 'helpModalOpen'>) => void;
  closeAllModals: () => void;
  setModalData: (data: Partial<UIState['modalData']>) => void;
  
  // Loading states
  setGlobalLoading: (loading: boolean, message?: string) => void;
  
  // Notification system
  addNotification: (notification: Omit<Notification, 'id' | 'timestamp'>) => string;
  removeNotification: (id: string) => void;
  clearNotifications: () => void;
  markNotificationAsRead: (id: string) => void;
  
  // Toast system
  showToast: (toast: Omit<Toast, 'id' | 'timestamp'>) => string;
  removeToast: (id: string) => void;
  clearToasts: () => void;
  
  // Form state management
  setFormState: (formId: string, state: Partial<UIState['formStates'][string]>) => void;
  setFormDirty: (formId: string, dirty: boolean) => void;
  setFormSubmitting: (formId: string, submitting: boolean) => void;
  setFormErrors: (formId: string, errors: Record<string, string>) => void;
  setFormValues: (formId: string, values: Record<string, any>) => void;
  clearFormState: (formId: string) => void;
  
  // View preferences
  setTheme: (theme: UIState['viewPreferences']['theme']) => void;
  setMapStyle: (style: string) => void;
  toggleCompactMode: () => void;
  updateViewPreferences: (preferences: Partial<UIState['viewPreferences']>) => void;
  
  // Responsive handling
  setScreenSize: (size: UIState['screenSize']) => void;
  setIsMobile: (mobile: boolean) => void;
  
  // Feature flags
  setFeatureFlag: (flag: string, value: boolean) => void;
  updateFeatureFlags: (flags: Partial<UIState['featureFlags']>) => void;
  
  // Utility actions
  resetUI: () => void;
  exportUIState: () => string;
  importUIState: (state: string) => boolean;
}

type UIStore = UIState & UIActions;

const initialState: UIState = {
  // Panel states
  sidebarOpen: true,
  layerPanelOpen: false,
  speciesPanelOpen: false,
  treePanelOpen: false,
  
  // Active panel
  activePanel: null,
  
  // Modal states
  speciesModalOpen: false,
  treeDetailsModalOpen: false,
  suggestionModalOpen: false,
  settingsModalOpen: false,
  helpModalOpen: false,
  
  // Modal data
  modalData: {},
  
  // Loading states
  globalLoading: false,
  loadingMessage: null,
  
  // Notifications
  notifications: [],
  maxNotifications: 10,
  
  // Toasts
  toasts: [],
  maxToasts: 5,
  
  // Form states
  formStates: {},
  
  // View preferences
  viewPreferences: {
    theme: 'auto',
    mapStyle: 'https://demotiles.maplibre.org/style.json',
    defaultZoom: 12,
    compactMode: false,
    showTooltips: true,
    language: 'en'
  },
  
  // Mobile/responsive
  isMobile: false,
  screenSize: 'lg',
  
  // Feature flags
  featureFlags: {
    enableExperimentalFeatures: false,
    enableAnalytics: true,
    enableOfflineMode: false
  }
};

export const useUIStore = create<UIStore>()(
  devtools(
    subscribeWithSelector(
      (set, get) => ({
        ...initialState,

        // Panel management
        toggleSidebar: () =>
          set(
            (state) => ({ sidebarOpen: !state.sidebarOpen }),
            false,
            'uiStore/toggleSidebar'
          ),

        setSidebarOpen: (open) =>
          set(
            { sidebarOpen: open },
            false,
            'uiStore/setSidebarOpen'
          ),

        setActivePanel: (panel) =>
          set(
            { activePanel: panel },
            false,
            `uiStore/setActivePanel/${panel}`
          ),

        togglePanel: (panel) =>
          set(
            (state) => ({ [panel]: !state[panel] }),
            false,
            `uiStore/togglePanel/${panel}`
          ),

        closeAllPanels: () =>
          set(
            {
              layerPanelOpen: false,
              speciesPanelOpen: false,
              treePanelOpen: false,
              activePanel: null
            },
            false,
            'uiStore/closeAllPanels'
          ),

        // Modal management
        openModal: (modal, data) =>
          set(
            (state) => ({
              [modal]: true,
              modalData: data ? { ...state.modalData, ...data } : state.modalData
            }),
            false,
            `uiStore/openModal/${modal}`
          ),

        closeModal: (modal) =>
          set(
            { [modal]: false },
            false,
            `uiStore/closeModal/${modal}`
          ),

        closeAllModals: () =>
          set(
            {
              speciesModalOpen: false,
              treeDetailsModalOpen: false,
              suggestionModalOpen: false,
              settingsModalOpen: false,
              helpModalOpen: false,
              modalData: {}
            },
            false,
            'uiStore/closeAllModals'
          ),

        setModalData: (data) =>
          set(
            (state) => ({
              modalData: { ...state.modalData, ...data }
            }),
            false,
            'uiStore/setModalData'
          ),

        // Loading states
        setGlobalLoading: (loading, message) =>
          set(
            { 
              globalLoading: loading, 
              loadingMessage: loading ? message || null : null 
            },
            false,
            'uiStore/setGlobalLoading'
          ),

        // Notification system
        addNotification: (notification) => {
          const id = `notification-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
          const newNotification: Notification = {
            ...notification,
            id,
            timestamp: Date.now(),
            autoClose: notification.autoClose ?? true,
            duration: notification.duration ?? 5000
          };

          set(
            (state) => ({
              notifications: [
                newNotification,
                ...state.notifications.slice(0, state.maxNotifications - 1)
              ]
            }),
            false,
            'uiStore/addNotification'
          );

          // Auto-remove notification if autoClose is enabled
          if (newNotification.autoClose) {
            setTimeout(() => {
              get().removeNotification(id);
            }, newNotification.duration);
          }

          return id;
        },

        removeNotification: (id) =>
          set(
            (state) => ({
              notifications: state.notifications.filter(n => n.id !== id)
            }),
            false,
            'uiStore/removeNotification'
          ),

        clearNotifications: () =>
          set(
            { notifications: [] },
            false,
            'uiStore/clearNotifications'
          ),

        markNotificationAsRead: (id) =>
          set(
            (state) => ({
              notifications: state.notifications.map(n =>
                n.id === id ? { ...n, read: true } : n
              )
            }),
            false,
            'uiStore/markNotificationAsRead'
          ),

        // Toast system
        showToast: (toast) => {
          const id = `toast-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
          const newToast: Toast = {
            ...toast,
            id,
            timestamp: Date.now(),
            duration: toast.duration ?? 3000,
            position: toast.position ?? 'top-right'
          };

          set(
            (state) => ({
              toasts: [
                newToast,
                ...state.toasts.slice(0, state.maxToasts - 1)
              ]
            }),
            false,
            'uiStore/showToast'
          );

          // Auto-remove toast
          setTimeout(() => {
            get().removeToast(id);
          }, newToast.duration);

          return id;
        },

        removeToast: (id) =>
          set(
            (state) => ({
              toasts: state.toasts.filter(t => t.id !== id)
            }),
            false,
            'uiStore/removeToast'
          ),

        clearToasts: () =>
          set(
            { toasts: [] },
            false,
            'uiStore/clearToasts'
          ),

        // Form state management
        setFormState: (formId, formState) =>
          set(
            (state) => ({
              formStates: {
                ...state.formStates,
                [formId]: {
                  ...state.formStates[formId],
                  ...formState
                }
              }
            }),
            false,
            `uiStore/setFormState/${formId}`
          ),

        setFormDirty: (formId, dirty) =>
          get().setFormState(formId, { isDirty: dirty }),

        setFormSubmitting: (formId, submitting) =>
          get().setFormState(formId, { isSubmitting: submitting }),

        setFormErrors: (formId, errors) =>
          get().setFormState(formId, { errors }),

        setFormValues: (formId, values) =>
          get().setFormState(formId, { values }),

        clearFormState: (formId) =>
          set(
            (state) => {
              const { [formId]: removed, ...rest } = state.formStates;
              return { formStates: rest };
            },
            false,
            `uiStore/clearFormState/${formId}`
          ),

        // View preferences
        setTheme: (theme) =>
          set(
            (state) => ({
              viewPreferences: { ...state.viewPreferences, theme }
            }),
            false,
            'uiStore/setTheme'
          ),

        setMapStyle: (style) =>
          set(
            (state) => ({
              viewPreferences: { ...state.viewPreferences, mapStyle: style }
            }),
            false,
            'uiStore/setMapStyle'
          ),

        toggleCompactMode: () =>
          set(
            (state) => ({
              viewPreferences: { 
                ...state.viewPreferences, 
                compactMode: !state.viewPreferences.compactMode 
              }
            }),
            false,
            'uiStore/toggleCompactMode'
          ),

        updateViewPreferences: (preferences) =>
          set(
            (state) => ({
              viewPreferences: { ...state.viewPreferences, ...preferences }
            }),
            false,
            'uiStore/updateViewPreferences'
          ),

        // Responsive handling
        setScreenSize: (size) =>
          set(
            { screenSize: size },
            false,
            'uiStore/setScreenSize'
          ),

        setIsMobile: (mobile) =>
          set(
            { isMobile: mobile },
            false,
            'uiStore/setIsMobile'
          ),

        // Feature flags
        setFeatureFlag: (flag, value) =>
          set(
            (state) => ({
              featureFlags: { ...state.featureFlags, [flag]: value }
            }),
            false,
            `uiStore/setFeatureFlag/${flag}`
          ),

        updateFeatureFlags: (flags) =>
          set(
            (state) => ({
              featureFlags: { ...state.featureFlags, ...flags }
            }),
            false,
            'uiStore/updateFeatureFlags'
          ),

        // Utility actions
        resetUI: () =>
          set(
            initialState,
            false,
            'uiStore/resetUI'
          ),

        exportUIState: () => {
          const { viewPreferences, featureFlags } = get();
          return JSON.stringify({ viewPreferences, featureFlags });
        },

        importUIState: (stateString) => {
          try {
            const importedState = JSON.parse(stateString);
            set(
              (state) => ({
                viewPreferences: { ...state.viewPreferences, ...importedState.viewPreferences },
                featureFlags: { ...state.featureFlags, ...importedState.featureFlags }
              }),
              false,
              'uiStore/importUIState'
            );
            return true;
          } catch {
            return false;
          }
        },
      })
    ),
    {
      name: 'ui-store',
    }
  )
);

// Specialized selectors
export const useSidebarOpen = () => 
  useUIStore(state => state.sidebarOpen);

export const useActivePanel = () => 
  useUIStore(state => state.activePanel);

export const useModalsOpen = () => 
  useUIStore(state => ({
    species: state.speciesModalOpen,
    treeDetails: state.treeDetailsModalOpen,
    suggestion: state.suggestionModalOpen,
    settings: state.settingsModalOpen,
    help: state.helpModalOpen
  }));

export const useGlobalLoading = () => 
  useUIStore(state => ({
    loading: state.globalLoading,
    message: state.loadingMessage
  }));

export const useNotifications = () => 
  useUIStore(state => state.notifications);

export const useToasts = () => 
  useUIStore(state => state.toasts);

export const useViewPreferences = () => 
  useUIStore(state => state.viewPreferences);

export const useFormState = (formId: string) => 
  useUIStore(state => state.formStates[formId] || {
    isDirty: false,
    isSubmitting: false,
    errors: {},
    values: {}
  });

export const useFeatureFlags = () => 
  useUIStore(state => state.featureFlags);

export const useResponsive = () => 
  useUIStore(state => ({
    isMobile: state.isMobile,
    screenSize: state.screenSize
  }));

// Action selectors
export const useUIActions = () => 
  useUIStore(state => ({
    toggleSidebar: state.toggleSidebar,
    setActivePanel: state.setActivePanel,
    openModal: state.openModal,
    closeModal: state.closeModal,
    addNotification: state.addNotification,
    showToast: state.showToast,
    setGlobalLoading: state.setGlobalLoading
  }));